import { attr, FASTElement, observable } from '@microsoft/fast-element';
import { buttonStyles, ButtonVariant, IButtonStyles } from '../../styles/patterns/button';
import { NatGenIcon } from '../icon/icon';
import { FormAssociated } from '../../foundation/form-associated';
import { applyMixins } from '../../foundation/apply-mixins';
import { ARIAGlobalStatesAndProperties } from '../../foundation/aria-global';
import { StartEnd } from '../start-end/start-end';
import { ButtonType } from './button.options';
import { buttonTemplate } from './button.template';
import { NatGenDesignSystem } from '../../natgen-design-system';

class _Button extends FASTElement {}
interface _Button extends FormAssociated {}

class FormAssociatedButton extends FormAssociated(_Button) {
    proxy = document.createElement("input");
}

/**
 * @element natgen-button
 */
export class NatGenButton extends FormAssociatedButton implements IButtonStyles {
    @attr
    public variant: ButtonVariant;

    @attr
    public color: string;

    public connectedCallback(): void {
        this.variant = this.variant || 'filled';
        this.color = this.color || 'neutral';

        super.connectedCallback();

        this.proxy.setAttribute("type", this.type);
    }

    /**
     * Determines if the element should receive document focus on page load.
     *
     * @public
     * @remarks
     * HTML Attribute: autofocus
     */
    @attr({ mode: "boolean" })
    public autofocus: boolean;

    /**
     * The id of a form to associate the element to.
     *
     * @public
     * @remarks
     * HTML Attribute: form
     */
    @attr({ attribute: "form" })
    public formId: string;

    /**
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element} for more details.
     *
     * @public
     * @remarks
     * HTML Attribute: formaction
     */
    @attr
    public formaction: string;
    protected formactionChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.formAction = this.formaction;
        }
    }

    /**
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element} for more details.
     *
     * @public
     * @remarks
     * HTML Attribute: formenctype
     */
    @attr
    public formenctype: string;
    protected formenctypeChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.formEnctype = this.formenctype;
        }
    }

    /**
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element} for more details.
     *
     * @public
     * @remarks
     * HTML Attribute: formmethod
     */
    @attr
    public formmethod: string;
    protected formmethodChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.formMethod = this.formmethod;
        }
    }

    /**
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element} for more details.
     *
     * @public
     * @remarks
     * HTML Attribute: formnovalidate
     */
    @attr({ mode: "boolean" })
    public formnovalidate: boolean;
    protected formnovalidateChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.formNoValidate = this.formnovalidate;
        }
    }

    /**
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element} for more details.
     *
     * @public
     * @remarks
     * HTML Attribute: formtarget
     */
    @attr
    public formtarget: "_self" | "_blank" | "_parent" | "_top";
    protected formtargetChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.formTarget = this.formtarget;
        }
    }

    /**
     * The button type.
     *
     * @public
     * @remarks
     * HTML Attribute: type
     */
    @attr
    public type: ButtonType;
    protected typeChanged(previous: ButtonType | undefined, next: ButtonType): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.type = this.type;
        }

        next === ButtonType.submit &&
            this.addEventListener("click", this.handleSubmission);
        previous === ButtonType.submit &&
            this.removeEventListener("click", this.handleSubmission);
        next === ButtonType.reset && this.addEventListener("click", this.handleFormReset);
        previous === ButtonType.reset &&
            this.removeEventListener("click", this.handleFormReset);
    }

    /**
     *
     * Default slotted content
     *
     * @public
     * @remarks
     */
    @observable
    public defaultSlottedContent: HTMLElement[];

    /** {@inheritDoc (FormAssociated:interface).validate} */
    public validate(): void {
        super.validate(this.control);
    }

    /**
     * Submits the parent form
     */
    private handleSubmission = () => {
        if (!this.form) {
            return;
        }

        const attached = this.proxy.isConnected;

        if (!attached) {
            this.attachProxy();
        }

        // Browser support for requestSubmit is not comprehensive
        // so click the proxy if it isn't supported
        typeof this.form.requestSubmit === "function"
            ? this.form.requestSubmit(this.proxy)
            : this.proxy.click();

        if (!attached) {
            this.detachProxy();
        }
    };

    /**
     * Resets the parent form
     */
    private handleFormReset = () => {
        this.form?.reset();
    };

    public control: HTMLButtonElement;

    /**
     * Applies 'icon-only' class when there is only an SVG in the default slot
     */
    public defaultSlottedContentChanged(oldValue, newValue): void {
        const slottedElements = this.defaultSlottedContent.filter(
            x => x.nodeType === Node.ELEMENT_NODE
        );
        if (slottedElements.length === 1 && (slottedElements[0] instanceof SVGElement || slottedElements[0] instanceof NatGenIcon)) {
            this.control.classList.add('icon-only');
        } else {
            this.control.classList.remove('icon-only');
        }
    }
}

/**
 * Includes ARIA states and properties relating to the ARIA button role
 *
 * @public
 */
export class DelegatesARIAButton {
    /**
     * See {@link https://www.w3.org/WAI/PF/aria/roles#button} for more information
     * @public
     * @remarks
     * HTML Attribute: aria-expanded
     */
    @attr({ attribute: "aria-expanded" })
    public ariaExpanded: "true" | "false" | string | null;

    /**
     * See {@link https://www.w3.org/WAI/PF/aria/roles#button} for more information
     * @public
     * @remarks
     * HTML Attribute: aria-pressed
     */
    @attr({ attribute: "aria-pressed" })
    public ariaPressed: "true" | "false" | "mixed" | string | null;
}

/**
 * Mark internal because exporting class and interface of the same name
 * confuses API documenter.
 * TODO: https://github.com/microsoft/fast/issues/3317
 * @internal
 */
export interface DelegatesARIAButton extends ARIAGlobalStatesAndProperties {}
applyMixins(DelegatesARIAButton, ARIAGlobalStatesAndProperties);

/**
 * Mark internal because exporting class and interface of the same name
 * confuses API documenter.
 * TODO: https://github.com/microsoft/fast/issues/3317
 * @internal
 */
export interface NatGenButton extends StartEnd, DelegatesARIAButton {}
applyMixins(NatGenButton, StartEnd, DelegatesARIAButton);

export const natGenButtonDefinition = NatGenButton.compose({
    name: `${NatGenDesignSystem.prefix}-button`,
    template: buttonTemplate(),
    styles: buttonStyles(':not([disabled])', '[disabled]'),
    shadowOptions: {
        delegatesFocus: true,
    },
});
